import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiArrowRight } = FiIcons;

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubmitted(true);
      // Handle newsletter subscription here
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
      }, 3000);
    }
  };

  return (
    <section className="py-24 relative bg-gray-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center space-y-12"
        >
          {/* Background Effects */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-orange/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-orange/3 rounded-full blur-3xl"></div>
          </div>

          <div className="relative z-10 space-y-8">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex justify-center">
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="w-20 h-20 bg-gradient-to-br from-primary-orange to-primary-dark flex items-center justify-center mb-6"
                >
                  <SafeIcon icon={FiMail} className="w-10 h-10 text-black" />
                </motion.div>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold">
                Join the <span className="gradient-text">Quantum Brief</span>
              </h2>
              
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Curated insights on scaling human potential through tech. Get the latest in AI-powered transformation, 
                delivered to your inbox weekly.
              </p>
            </motion.div>

            {/* Newsletter Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
            >
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="w-full px-6 py-4 bg-gray-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-primary-orange transition-colors duration-200 text-lg"
                      required
                    />
                    <motion.div
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: email ? 1 : 0 }}
                      className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-primary-orange to-primary-dark"
                    ></motion.div>
                  </div>
                  
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-primary-orange text-black px-8 py-4 font-semibold text-lg hover:bg-primary-dark transition-all duration-200 flex items-center justify-center space-x-2 group"
                  >
                    <span>Subscribe</span>
                    <SafeIcon 
                      icon={FiArrowRight} 
                      className="w-5 h-5 group-hover:translate-x-1 transition-transform" 
                    />
                  </motion.button>
                </form>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="glass-effect p-8 space-y-4"
                >
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                    <SafeIcon icon={FiMail} className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-green-400">Welcome to the Quantum Brief!</h3>
                  <p className="text-gray-300">
                    You're now part of an exclusive community focused on scaling human potential through technology.
                  </p>
                </motion.div>
              )}
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-gray-400 text-sm"
            >
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>5,000+ subscribers</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Weekly insights</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Unsubscribe anytime</span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;